import React, { Component } from 'react';
import PropTypes from 'prop-types';
import shortid from 'shortid';
import { logEvent } from '@zr/utilities';

import uploadResume from '@zr/utilities/api/uploadResume';

import { ACCEPTED_EXTENSIONS, ACCEPTED_EXTENSIONS_ERROR, ACCEPTED_TYPES } from './constants';

import './ResumeUpload.less';

const noop = () => {};

export class ResumeUpload extends Component {
  static propTypes = {
    children: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.element,
      PropTypes.arrayOf(PropTypes.node),
    ]),
    className: PropTypes.string,
    disabled: PropTypes.bool,
    dryRun: PropTypes.bool,
    id: PropTypes.string,
    labelButtonClassName: PropTypes.string,
    labelClassName: PropTypes.string,
    logEvent: PropTypes.shape({
      component: PropTypes.string.isRequired,
      field: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }),
    // profile_method and resume_method
    method: PropTypes.string,
    text: PropTypes.string,

    /**
     * Using onChange will intercept the file upload process, so the selected
     * file will no longer be automatically uploaded. However, onChange()
     * provides this.uploadFile() as an argument, so uploadFile() can be called
     * manually to execute the API request to upload the file.
     *
     * @example
     * handleChange = uploadFunction => uploadFunction();
     */
    onChange: PropTypes.func,

    onClick: PropTypes.func,
    onUploadEnd: PropTypes.func,
    onUploadError: PropTypes.func,
    onUploadStart: PropTypes.func,
    overwrite: PropTypes.bool,
    replaceDefault: PropTypes.bool,
    responseId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    useResumePredictorValidation: PropTypes.bool,
  }

  static defaultProps = {
    children: null,
    className: '',
    disabled: false,
    dryRun: false,
    id: null,
    labelButtonClassName: '',
    labelClassName: '',
    logEvent: null,
    method: 'fe.zr-components.ResumeUpload',
    onChange: noop,
    onClick: noop,
    onUploadEnd: noop,
    onUploadError: noop,
    onUploadStart: noop,
    overwrite: false,
    replaceDefault: true,
    responseId: null,
    text: null,
    useResumePredictorValidation: false,
  }

  state = {
    uploading: false,
    value: null,
  }

  UNSAFE_componentWillMount() {
    const id = `ResumeUpload_${shortid.generate()}`;
    this.setState({ id });
  }

  handleChange = (e) => {
    e.preventDefault();
    const { onChange, onUploadError } = this.props;
    const fileList = e.target.files;

    // if file is chosen, and then second dialog is canceled and no file is
    // chosen, the handler will run with an empty file list, so abort
    if (!fileList.length) { return; }

    const [file] = e.target.files;

    // raise error if file extension isn't one of the accepted types
    const extension = file.name.split('.').pop();
    if (!ACCEPTED_EXTENSIONS.includes(extension.toLowerCase())) {
      onUploadError(Error(ACCEPTED_EXTENSIONS_ERROR));

      // reset input value
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.addEventListener('load', () => {
      this.setState({
        // uploading: true,
        value: reader.result,
      }, () => {
        if (onChange !== noop) {
          onChange(this.uploadFile.bind(this, file));
        } else {
          this.uploadFile(file);
        }
      });
    });

    reader.readAsDataURL(file);

    // allow the same file to be selected again
    e.target.value = null;
  }

  handleLabel = (event) => {
    const { onClick } = this.props;
    if (event.type === 'click') {
      onClick(event);
      this.callLogEvent();
    }

    // add a11y to trigger upload dialog by pressing Space or Enter
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.input.click();
      onClick(event);
      this.callLogEvent();
    }
  }

  callLogEvent() {
    const { logEvent: logEventProp } = this.props;
    if (!logEventProp) { return; }
    logEvent(logEventProp.type, {
      component: logEventProp.component,
      field: logEventProp.field,
    });
  }

  async uploadFile(file) {
    const {
      dryRun, onUploadEnd, onUploadError, onUploadStart, overwrite, replaceDefault, responseId,
      useResumePredictorValidation, method,
    } = this.props;

    try {
      const { value } = this.state;

      onUploadStart();
      let profile = { resume: {} };
      if (!dryRun) {
        profile = await uploadResume(value, file.name, {
          overwrite,
          profileMethod: method,
          replaceDefault,
          responseId,
          resumeMethod: method,
          useResumePredictorValidation,
        });
      }
      onUploadEnd(profile, overwrite);
    } catch (error) {
      const { value } = this.state;

      // sends error, any resume info if parent component tries to submit again or do something with the current data
      onUploadError(error, file, value);
    } finally {
      // this line is currently commented because setState() is being called
      // on an unmounted component (ResumeUpload is unmounted when the profile
      // is loading)
      // this.state.uploading has no visible effect while the profile UI is
      // blocked anyway, but this behavior is subject to change
      // this.setState({ uploading: false });
    }
  }

  render() {
    const { children, className, disabled, id: overrideId, labelButtonClassName, labelClassName, text } = this.props;
    const { id: generatedId, uploading } = this.state;

    // parent component might want to override the id to refer to this input
    const id = overrideId || generatedId;

    return (
      <span className={`ResumeUpload ${className}`}>
        <input
          id={id}
          type="file"
          name="ResumeUpload"
          onChange={this.handleChange}
          disabled={disabled || uploading}
          accept={ACCEPTED_TYPES}
          ref={(element) => { this.input = element; }}
          tabIndex={-1}
        />
        <label htmlFor={id} className={`${labelClassName}${uploading ? ' uploading' : ''}`}>
          <span
            className={labelButtonClassName}
            onClick={this.handleLabel}
            onKeyPress={this.handleLabel}
            role="button"
            tabIndex={0}
          >
            {children || text}
          </span>
        </label>
      </span>
    );
  }
}

export default ResumeUpload;
